import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0416 from "../lesson-16/lesson";

const linkLesson0416 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0416.id);

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>The principle of electrostatic transduction [1, 2] is based on the electro-mechanical interactions between the two electrodes of a parallel plate-like capacitor, one of which is set in vibration (by a sound wave in the case of a sensor, or by an electrostatic force in the case of an actuator) while the other remains motionless. Electrostatic microphones are widely used for various applications such as acoustic measurements or sound recording. Their miniaturisation has allowed the realisation of micro electro-acoustical systems for telephony or hearing aids. The use of electrostatic loudspeakers, although encountered in various applications including hi-end quality sound reproduction, is less widespread.
<h3 class=sectionHead>Capacitor theory</h3>
<p>
   Let us consider a parallel plate capacitor consisting of two fixed electrodes of area
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>
   separated by an electrical insulator (also called dielectric) of thickness
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   (see <a href=# data-img-alt="Figure 1. schematic view of a parallel plate capacitor." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-1.svg>Figure 1</a>).
   <figure class=figure>
<p><img alt="Figure 1. schematic view of a parallel plate capacitor." src=${config.ASSETS_URL}/04/10/theory/figure-1.svg><figcaption class=caption>Figure 1. schematic view of a parallel plate capacitor.</figcaption></figure>
<p>
   When inserted into a charging circuit (see <a href=# data-img-alt="Figure 2. Capacitor charging circuit." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-2.svg>Figure 2</a>), the capacitor will charge through the resistor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>R</mi>
   </math>. Negative electrical charge accumulates on one of the two electrodes. The amount of negative charge accumulated on this negative electrode is denoted
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <mi>q</mi>
   </math>. The amount of negative charge given off by the opposite electrode can be seen as an accumulation of positive charge
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>+</mo>
      <mi>q</mi>
   </math>. Consequently, an electrical potential difference
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>u</mi>
   </math>
   arises between the two electrodes. At the end of the charging process, the capacitor’s voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>u</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   gets stabilized to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>. The electrical capacitance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   of this capacitor is defined as the ratio between the charge
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>q</mi>
   </math>
   accumulated on the positive electrode and the potential difference
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   between the two electrodes
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>q</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   The capacitance of a capacitor depends on the geometric characteristics of the capacitor (electrode area
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>
   and inter-electrode distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>) and on the dielectric constant (or permittivity)
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜀</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>𝜀</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>𝜀</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   of the electrical insulator, with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>𝜀</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   the vacuum permittivity and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>𝜀</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   the dielectric relative permittivity. For a parallel plate capacitor, the capacitance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>𝜀S</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(2)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 2. Capacitor charging circuit." src=${config.ASSETS_URL}/04/10/theory/figure-2.svg>
      <figcaption class=caption>Figure 2. Capacitor charging circuit.</figcaption>
</figure>
<p>
   When charged, the two electrodes of the capacitor are subjected to an electrostatic force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   and attract each other. The electrostatic force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   between the two electrodes is written as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>q</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(3)
</table>
<h3 class=sectionHead>General principle of electrostatic transduction</h3>
<p>Electrostatic transduction is based on the use of a variable capacitor. In its simplest form, an electrostatic transducer consists of a parallel-plate capacitor, one electrode of which may be moved towards or away from the second electrode, which remains stationary (<a href=# data-img-alt="Figure 3. Schematic diagram of an electrostatic transducer." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-3.svg>Figure 3</a>). The moving electrode acts as the transducer’s diaphragm. Let us consider that this electrostatic device is electrically charged.
<p>In the case of a <strong><span class=cmbx-10>sensor</span></strong>, an acoustic wave sets the diaphragm into vibration, inducing a variation of the distance between the two electrodes. This results in a change in the electrical capacitance of the capacitor according to Eq. (2), which in turn results in a change in the electrical potential difference between the two electrodes according to Eq. (1). Note that the variable capacitor must necessarily be charged to obtain a variation of the electric potential difference.
<p>
   In the case of an <strong><span class=cmbx-10>actuator</span></strong>, a change in the voltage across the capacitor results in a change in the electrical charge on the electrodes according to Eq. (1). This results in a change in the electrostatic force between the two electrodes according to Eq. (3). Thus, the two electrodes are more or less attracted to each other, resulting in a vibration of the diaphragm.
   <figure class=figure>
<p><img alt="Figure 3. Schematic diagram of an electrostatic transducer." src=${config.ASSETS_URL}/04/10/theory/figure-3.svg><figcaption class=caption>Figure 3. Schematic diagram of an electrostatic transducer.</figcaption></figure>
<h3 class=sectionHead>Electrostatic force</h3>
<h4 class=subsectionHead>Case of fixed electrodes</h4>
<p>
   As a first step, let us consider that the two electrodes of the electrostatic transducer are fixed. The electrostatic system under study is then a simple capacitor. Let us consider that this capacitor is connected to a variable voltage generator with internal resistance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
   </math>
   and open circuit voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   (<a href=# data-img-alt="Figure 4. Capacitor powered by an AC voltage generator." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-4.svg>Figure 4</a>). The voltage across the capacitor is denoted
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>.
   <figure class=figure>
<p><img alt="Figure 4. Capacitor powered by an AC voltage generator." src=${config.ASSETS_URL}/04/10/theory/figure-4.svg><figcaption class=caption>Figure 4. Capacitor powered by an AC voltage generator.</figcaption></figure>
<p>
   The variable electric voltage results in a variation of the charge
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>q</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   of the capacitor
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>q</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   Therefore, the two electrodes are subjected to the variable electrostatic force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>q</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>u</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   Equation (5) is inherently non-linear, as the resultant force is proportional to the square of the electrical voltage across the capacitor. To illustrate this non-linear behaviour, let us consider the case of a sinusoidal excitation voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   of frequency
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
               </mrow>
            </msub>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the angular frequency. In the steady state, the voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   varies sinusoidally, as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>t</mi>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>φ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>g</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
   </math>
   and where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>φ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi class=qopname>arctan</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>. The electrostatic force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is then written
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>ωt</mi>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>φ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mn>2</mn>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>φ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   This force consists of both a DC component and an alternating component at the frequency
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>2</mn>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>. <a href=# data-img-alt="Figure 5. Nonlinear behavior of electrostatic force." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-5.svg>Figure 5</a> shows the temporal evolution of the signals
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and illustrates the non-linear behaviour of electrostatic force.
   <figure class=figure>
<p><img alt="Figure 5. Nonlinear behavior of electrostatic force." src=${config.ASSETS_URL}/04/10/theory/figure-5.svg><figcaption class=caption>Figure 5. Nonlinear behavior of electrostatic force.</figcaption></figure>
<p>
   In order to avoid this nonlinear behaviour, a classical solution is the use of a DC bias voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>, obtained for example from the bias circuit given in <a href=# data-img-alt="Figure 6. Bias voltage circuit." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-6.svg>Figure 6</a>. Thus, in steady state, the voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   across the capacitor is composed of both a DC component
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and an AC component
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>u</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>u</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>The electrostatic force between the electrodes is then written
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>U</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mi>u</mi>
                        <mo class=MathClass-open>(</mo>
                        <mi>t</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(10)
</table>
<p>that is
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>u</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>u</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   In practice, the bias voltage is set so that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≫</mo>
      <mi>u</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>. Under these conditions, the third term on the right-hand side of the Eq. (11) is negligible compared to the other two terms. The electrostatic force can be then written
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>≃</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>u</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   The first term on the right hand side of the Eq. (12) is the DC component of the electrostatic force due to the bias voltage, while the second term is the linearised AC component.
   <figure class=figure>
<p><img alt="Figure 6. Bias voltage circuit." src=${config.ASSETS_URL}/04/10/theory/figure-6.svg><figcaption class=caption>Figure 6. Bias voltage circuit.</figcaption></figure>
<p>
   Note that in the case of loudspeakers, the voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>u</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is much higher than in the case of microphones. Therefore, bias voltages used for loudspeakers are also much higher than the ones used for microphones. Typical bias voltage is
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>48</mn> 
   </math> V for a recording microphone or
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>200</mn>
   </math> V
   for a measurement microphone, while the bias voltage can reach
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>5000</mn>
   </math> V for a loudspeaker.
<h4 class=subsectionHead>Case of a moving electrode</h4>
<p>
   Let us now consider that the electrostatic system under study is a transducer consisting of a fixed electrode and a moving diaphragm. In this case, the diaphragm is set in motion by the electrostatic force exerted on it. Let
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ξ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   be the displacement of the diaphragm. Due to this displacement, the distance between electrodes varies with time. According to the axe defined in <a href=# data-img-alt="Figure 7. Definition of orientation." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-7.svg>Figure 7</a>, this distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   can be written
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>d</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mi>ξ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(13)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 7. Definition of orientation." src=${config.ASSETS_URL}/04/10/theory/figure-7.svg>
      <figcaption class=caption>Figure 7. Definition of orientation.</figcaption>
</figure>
<p>
   Thus, the capacitance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>C</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   of the capacitor also varies with time, and is written
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>C</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>𝜀S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(14)
</table>
<p>The electrostatic force between the electrodes is then written
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>q</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>C</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
                  <mi>d</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>C</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
                  <msubsup>
                     <mrow>
                        <mi>u</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>d</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>𝜀S</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>u</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>u</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(15)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>u</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>. Electrostatic force can still be written as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>[</mo>
                        <msub>
                           <mrow>
                              <mi>U</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mi>u</mi>
                        <mo class=MathClass-open>(</mo>
                        <mi>t</mi>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-close>]</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>[</mo>
                        <msub>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>−</mo>
                        <mi>ξ</mi>
                        <mo class=MathClass-open>(</mo>
                        <mi>t</mi>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-close>]</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(16)
</table>
<p>
   Considering that the displacement
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ξ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is very small compared to the distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>, the expression of the electrostatic force can be simplified to
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>≃</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>[</mo>
                        <msub>
                           <mrow>
                              <mi>U</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mi>u</mi>
                        <mo class=MathClass-open>(</mo>
                        <mi>t</mi>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-close>]</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mrow>
                     <mo fence=true form=prefix>[</mo>
                     <mrow>
                        <mn>1</mn>
                        <mo class=MathClass-bin>+</mo>
                        <mfrac>
                           <mrow>
                              <mi>ξ</mi>
                              <mo class=MathClass-open>(</mo>
                              <mi>t</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>d</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>0</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>]</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(17)
</table>
<p>
   After expansion and keeping only the first order of the variables
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>u</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ξ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   (because
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>u</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≪</mo>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <mi>ξ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≪</mo>
      <mn>1</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>u</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mi>ξ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≪</mo>
      <mi>U</mi>
      <mn>0</mn>
       <mo>)</mo>
   </math>, it yields
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>≃</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>u</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
            <mi>ξ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(18)
</table>
<p>
   The first term on the right-hand side of the equation corresponds to the DC component and comes from the bias voltage, while the following terms correspond to the linearised AC components. Keeping only the AC components, and writing that the velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>v</mi>
   </math>
   of the membrane is the time derivative of the displacement
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ξ</mi>
   </math>, with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>v</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>dξ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>dt</mi>
         </mrow>
      </mfrac>
   </math>, the electro-mechanical coupling equation (18) is written, in the harmonic regime
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>U</mi>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msubsup>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
            <mi>V</mi>
          </math>
      <td class=eq-no>(19)
</table>
<p>or
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mi>U</mi>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>b</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>V</mi>
         </math>
      <td class=eq-no>(20)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the electrostatic coupling coefficient.
<h3 class=sectionHead>Electrical behavior</h3>
<p>
   When the diaphragm of an electrostatic transducer undergoes a displacement
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ξ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>, the distance between the two electrodes varies according to the relation
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <mi>ξ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>. This results in a change in the electrical capacitance of the biased transducer which induces a change in the electrical state of the circuit into which it is inserted. The variation of the capacitance of the transducer is given by
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>C</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>𝜀S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>𝜀S</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>−</mo>
                  <mi>ξ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>𝜀S</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <mi>ξ</mi>
                        <mo class=MathClass-open>(</mo>
                        <mi>t</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <mi>ξ</mi>
                        <mo class=MathClass-open>(</mo>
                        <mi>t</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(21)
</table>
<p>
   In general, the displacement
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ξ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is very small compared to the distance at rest
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>, so
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ξ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≪</mo>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>. Thus, the capacity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>C</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   can be written
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>C</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>≃</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mi>ξ</mi>
                        <mo class=MathClass-open>(</mo>
                        <mi>t</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
          </math>
      <td class=eq-no>(22)
</table>
<p>
   Taking into account that the voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   across the electrostatic transducer is the sum of a static bias voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and a variable component
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>u</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>u</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>, the electric charge
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>q</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>C</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is given by
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>q</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>C</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mi>ξ</mi>
                        <mo class=MathClass-open>(</mo>
                        <mi>t</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>u</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(23)
</table>
<p>that is
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>q</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>u</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>ξ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>ξ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi>u</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(24)
</table>
<p>Neglecting the second order terms, this relation becomes
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>q</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>≃</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>u</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>ξ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(25)
</table>
<p>
   Keeping only the alternating components of this equation, and noting that the intensity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>i</mi>
   </math>
   of the electric current is the time derivative of the charge
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>q</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>i</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>d</mi>
            <msub>
               <mrow>
                  <mi>q</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi mathvariant=italic>dt</mi>
         </mrow>
      </mfrac>
   </math>, the time derivative of the equation (25) is written
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>i</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>≃</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>du</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>dt</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>v</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
          </math>
      <td class=eq-no>(26)
</table>
<p>In the harmonic regime, this equation is written
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>I</mi>
            <mo class=MathClass-rel>≃</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>U</mi>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>U</mi>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mi>V</mi>
         </math>
      <td class=eq-no>(27)
</table>
<h3 class=sectionHead>Coupling equations and equivalent networks</h3>
<p>In summary, the two coupling equations that describe electrostatic transduction are, in harmonic regime,
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mi>U</mi>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>b</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>V</mi>
         </math>
      <td class=eq-no>(28)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>I</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>U</mi>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mi>V</mi>
         </math>
      <td class=eq-no>(29)
</table>
<p>
   These coupling equations can be put in the form of the equivalent electrical networks given in Figures <a href=# data-img-alt="Figure 8. First electric network equivalent to electrostatic transduction." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-8.svg>8</a> and <a href=# data-img-alt="Figure 9. Second electric network equivalent to electrostatic transduction." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-9.svg>9</a>.
   <figure class=figure>
<p><img alt="Figure 8. First electric network equivalent to electrostatic transduction." src=${config.ASSETS_URL}/04/10/theory/figure-8.svg><figcaption class=caption>Figure 8. First electric network equivalent to electrostatic transduction.</figcaption></figure><figure class=figure><p><img alt="Figure 9. Second electric network equivalent to electrostatic transduction." src=${config.ASSETS_URL}/04/10/theory/figure-9.svg><figcaption class=caption>Figure 9. Second electric network equivalent to electrostatic transduction.</figcaption></figure>
<p>
   The negative capacitance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msubsup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
         </mrow>
      </mfrac>
   </math>
   shown in the electric network in <a href=# data-img-alt="Figure 9. Second electric network equivalent to electrostatic transduction." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-9.svg>Figure 9</a> can be seen as a negative mechanical compliance due to the electrostatic force between the membrane and the fixed back electrode. Indeed, unlike an elastic restoring force whose effect can be accounted for by positive mechanical compliance, the electrostatic force tends to draw the diaphragm more towards the rear electrode as the diaphragm moves towards this fixed electrode. Similarly, as the diaphragm moves away from the back electrode, the elastic restoring force tends to return the diaphragm to its resting position, while at the same time the electrostatic force that attracts the diaphragm to the back electrode decreases as the two electrodes are further apart.
<p>
   The equivalent electrical networks given in <a href=# data-img-alt="Figure 8. First electric network equivalent to electrostatic transduction." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-8.svg>Figure 8</a> and <a href=# data-img-alt="Figure 9. Second electric network equivalent to electrostatic transduction." data-img-src=${config.ASSETS_URL}/04/10/theory/figure-9.svg>Figure 9</a> can be used to model the behaviour of electrostatic transducers, sensors or actuators. They are used in <a href="${linkLesson0416}" target="_blank">Lesson 16</a> for the study of electrostatic microphones.
<h3 class=sectionHead>Conclusion</h3>
<p>In future Lessons, we will use the theory of electrostatic coupling developed in this Lesson to describe the behavior of electrostatic transducers, in particular electrostatic microphones.
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>AC
  <dd>Alternating Current
  <dt>DC
  <dd>Direct Current
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-merhaut1981theory>[1]
   <dd class=thebibliography id=bib-1>
      <p>Josef Merhaut. <span class=cmti-10>Theory of electroacoustics</span>. McGraw-Hill College, 1981. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0070414785.
   <dt class=thebibliography id=X0-Rossi1988>[2]
   <dd class=thebibliography id=bib-2>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0890062555.
</dl>`;

export default theory;
